/**
 * The connected Revit / AutoCAD, at the top of the sidebar.
 *
 * It was a line in a footer strip, and the team's report was that the thing the
 * whole app drives sat at the very bottom of the window, where it was easy to
 * miss. So it leads the sidebar now: **one card, one row per connected vendor**
 * (Revit, then AutoCAD), each row the document over its product and each with
 * its own dropdown of that vendor's instances (#5). One card rather than a card
 * per vendor because it answers one question — what is this app connected to —
 * and two bordered boxes pushed the menu down for no extra meaning.
 *
 * "Cannot detect" and "detected none" stay different sentences here, as they
 * were in the footer (Corrections #23): a card that only knew how to say "no
 * connected instances" would say it whether or not anything was checked.
 *
 * **The agent's status is silent while it is ready.** "agent ready" was a
 * permanent line that told the user nothing they could act on; the agent only
 * earns space when it is starting or down, using the same status row as CAD
 * detection. The element itself is always in the document — hidden, not removed,
 * when ready — because the desktop's smoke check reads `[data-status="agent"]`
 * and its dot's colour to decide whether the app came up.
 *
 * `InstanceChips` is the same selection for the title bar. In the desktop window
 * the chips are where the instance lives — always visible, sidebar open or not —
 * and the card renders only its hidden agent element (`inTitlebar`). The card's
 * rows are for a plain browser, which has no title bar.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { AgentTarget } from "../lib/agentClient";
import type { HostsView } from "../hooks/useHosts";
import { useLocale } from "../i18n/LocaleProvider";
import { hostsTone, instanceParts, type HostKind, type Tone } from "../lib/hostStatus";
import { selectionFor, type InstanceRestriction, type Selections } from "../lib/selectedInstance";
import type { CadInstance } from "../bimgo";
import { InstancesPopover } from "./InstancesPopover";
import { Toast } from "./Toast";

/** Shared colours for connection and Agent status. */
const TONE_COLOURS = {
  ok: "#2ea043",
  warn: "#d29922",
  bad: "#f85149",
  idle: "#a8a29e",
} as const;

const VENDOR_COLOURS: Record<HostKind, string> = {
  revit: "#186BFF",
  autocad: "#E51050",
};

function agentTone(agent: AgentTarget): Tone {
  if (agent.ok) return "ok";
  return agent.reason === "failed" || agent.reason === "stopped" ? "bad" : "warn";
}

type Translate = ReturnType<typeof useLocale>["t"];

function agentLabel(agent: AgentTarget, t: Translate): string {
  if (agent.ok) return t("connection.agent.ready");
  if (agent.reason === "failed" && agent.detail) return t("connection.agent.failedDetail", { detail: agent.detail });
  return t(`connection.agent.${agent.reason}`);
}

function hostsLabel(hosts: HostsView, t: Translate): string {
  return t(`connection.hosts.${hosts.kind === "loaded" ? "none" : hosts.kind}`);
}

function Dot({ colour }: { colour: string }) {
  return <span style={{ color: colour }}>●</span>;
}

/** One presentation for connection state, in both the sidebar and title bar. */
function ConnectionStatus({
  variant, tone, label, status, hidden = false,
}: {
  variant: "row" | "chip";
  tone: Tone;
  label: string;
  status?: "agent" | "hosts";
  hidden?: boolean;
}) {
  return (
    <div
      data-status={status}
      className={hidden ? "visually-hidden" : `connection-status is-${variant} is-${tone}${variant === "chip" ? " titlebar-chip is-empty" : " instance-row is-empty"}`}
      role={hidden ? undefined : "status"}
      aria-hidden={hidden || undefined}
      title={label}
    >
      {/* The smoke check reads the first span's colour. */}
      <Dot colour={TONE_COLOURS[tone]} />
      <span className="connection-status-label">{label}</span>
    </div>
  );
}

interface InstanceControls {
  hosts: HostsView;
  selections: Selections;
  /** The instances of each vendor (`groupByVendor`). */
  grouped: Partial<Record<HostKind, CadInstance[]>>;
  /** A chat turn is streaming — every vendor's selection is pinned until it ends (D9). */
  busy: boolean;
  pickFor: (kind: HostKind) => (sessionId: string) => void;
  /** An open AutoCAD skill's supported years: the AutoCAD picker offers only those. */
  restriction: InstanceRestriction | null;
}

export function InstanceCard({
  agent,
  hosts,
  selections,
  grouped,
  busy,
  pickFor,
  restriction,
  inTitlebar,
}: InstanceControls & { agent: AgentTarget; inTitlebar: boolean }) {
  const { t } = useLocale();
  const previousSelections = useRef<Selections | null>(null);
  const [notice, setNotice] = useState<{ text: string } | null>(null);

  // Observe the resolved targets, so both explicit picks and automatic fallbacks
  // report what subsequent operations will actually use. A failed detection is
  // not evidence that a target closed; keep the last successful observation.
  useEffect(() => {
    if (hosts.kind !== "loaded") return;
    const previous = previousSelections.current;
    previousSelections.current = selections;
    if (!previous) return;
    const changes: string[] = [];
    for (const kind of selections.vendors) {
      const before = selectionFor(previous, kind).instance;
      const after = selectionFor(selections, kind).instance;
      if (!before || !after || before.sessionId === after.sessionId) continue;
      const parts = instanceParts(after, t("about.untitled"));
      const key = hosts.instances.some((instance) => instance.sessionId === before.sessionId)
        ? "instance.switched" : "instance.autoSwitched";
      changes.push(t(key, { product: parts.product, name: parts.title }));
    }
    if (changes.length > 0) setNotice({ text: changes.join(" ") });
  }, [hosts, selections, t]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const connected = hosts.kind === "loaded" && selections.vendors.length > 0;
  // With the chips in the title bar, they say the agent's state too; this
  // element stays only for the smoke check.
  const agentHidden = agent.ok || inTitlebar;
  const agentStatus = <ConnectionStatus variant="row" tone={agentTone(agent)} label={agentLabel(agent, t)} status="agent" hidden={agentHidden} />;

  return (
    <>
      {inTitlebar ? agentStatus : (
        <section className="instance-card" aria-label={t("hosts.connectedTitle")}>
          {connected ? (
            // The lock is global — a turn's tool set was built from every
            // selection (D9) — so every row is pinned by the one `busy`.
            selections.vendors.map((kind) => (
              <InstancesPopover
                key={kind}
                variant="row"
                vendor={kind}
                instances={grouped[kind] ?? []}
                selection={selectionFor(selections, kind)}
                busy={busy}
                onSelect={pickFor(kind)}
                dot={<Dot colour={selectionFor(selections, kind).instance ? VENDOR_COLOURS[kind] : TONE_COLOURS.idle} />}
                restriction={restriction}
              />
            ))
          ) : (
            // Not a control: a row that opens an empty list teaches people not to click.
            <ConnectionStatus variant="row" tone={hosts.kind === "unsupported" || hosts.kind === "loading" ? "warn" : hostsTone(hosts)} label={hostsLabel(hosts, t)} status="hosts" />
          )}
          {agentStatus}
        </section>
      )}
      {notice ? createPortal(<Toast tone="ok" text={notice.text} onClose={() => setNotice(null)} />, document.body) : null}
    </>
  );
}

/**
 * The title bar's chips: one per connected vendor, the same dropdowns as the
 * card. Nothing connected is a single quiet chip saying so, and an agent that is
 * not ready gets a chip of its own — the card's agent line is hidden while the
 * chips are drawn, so this is where that is said.
 */
export function InstanceChips({ agent, hosts, selections, grouped, busy, pickFor, restriction }: InstanceControls & { agent: AgentTarget }) {
  const { t } = useLocale();
  const connected = hosts.kind === "loaded" && selections.vendors.length > 0;

  return (
    <>
      {connected ? (
        selections.vendors.map((kind) => (
          <InstancesPopover
            key={kind}
            variant="chip"
            vendor={kind}
            instances={grouped[kind] ?? []}
            selection={selectionFor(selections, kind)}
            busy={busy}
            onSelect={pickFor(kind)}
            dot={<Dot colour={selectionFor(selections, kind).instance ? VENDOR_COLOURS[kind] : TONE_COLOURS.idle} />}
            restriction={restriction}
          />
        ))
      ) : (
        <ConnectionStatus variant="chip" tone={hosts.kind === "unsupported" || hosts.kind === "loading" ? "warn" : hostsTone(hosts)} label={hostsLabel(hosts, t)} />
      )}
      {agent.ok ? null : (
        <ConnectionStatus variant="chip" tone={agentTone(agent)} label={agentLabel(agent, t)} />
      )}
    </>
  );
}
